import React, { useEffect, useState } from "react"
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  styled,
  Menu,
  MenuItem
} from "@mui/material"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import Header from "components/Header/Header"
import AppButton from "components/StyledComponents/AppButton"
import AppPagination from "components/StyledComponents/AppPagination"
import AppTextField from "components/StyledComponents/AppTextField"
import CommonModal from "components/CommonModal"
import DeleteConfirmationDialog from "components/DeleteConfirmationDialog"
import { createBreadcrumbs } from "helpers/breadCrumbs"
import { useHistory } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import {
  getPreshiftsAction,
  createPreshiftAction,
  getPreshiftDetailAction,
  deletePreshiftAction,
  unpublishPreshiftAction,
  copyPreshiftAction
} from "redux/superAdmin/actions/user"
import { preshiftColumns } from "helpers/constant"
import moment from "moment"

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: "12px 24px"
}))

const CustomTable = styled(Table)(({ theme }) => ({
  backgroundColor: theme.palette.background.main,
  borderCollapse: "collapse !important",
  "& .MuiTableCell-root": {
    padding: "12px 24px 12px 0px",
    backgroundColor: theme.palette.background.main,
    borderBottom: "none",
    boxSizing: "border-box",
    "&:last-child": {
      paddingRight: 0
    }
  },
  "& .MuiTableRow-root": {
    borderBottom: `2px solid ${theme.palette.drawer.main}`,
    "&:last-child": {
      borderBottom: "none"
    }
  },
  "& .MuiTableCell-head": {
    verticalAlign: "bottom",
    position: "sticky",
    borderBottom: `2px solid ${theme.palette.drawer.main}`,
    "&:last-child": {
      paddingRight: 0
    }
  }
}))

const PreshiftManagement = () => {
  const theme = useTheme()
  const history = useHistory()
  const dispatch = useDispatch()

  const [breadCrumbs, setBreadcrumbs] = useState([])
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedPreshift, setSelectedPreshift] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [isDateModalOpen, setIsDateModalOpen] = useState(false)
  const [preshiftDate, setPreshiftDate] = useState(new Date().toISOString().split("T")[0])
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false)
  const [copySourceDate, setCopySourceDate] = useState(new Date().toISOString().split("T")[0])
  const { preshiftList: rawPreshiftList } = useSelector((state) => state.superAdmin || {})
  const preshiftList = Array.isArray(rawPreshiftList)
    ? rawPreshiftList
    : rawPreshiftList?.data || []
  const openMenu = Boolean(anchorEl)

  useEffect(() => {
    document.title = "Preshift"
    setBreadcrumbs(createBreadcrumbs())
    dispatch(getPreshiftsAction())
  }, [])

  const handleMenuClick = (event, preshift) => {
    setAnchorEl(event.currentTarget)
    setSelectedPreshift(preshift)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleAddPreshift = () => {
    setIsDateModalOpen(true)
  }

  const handleConfirmDate = () => {
    dispatch(
      createPreshiftAction({
        payload: { date: preshiftDate },
        callback: (res) => {
          setIsDateModalOpen(false)
          const id = res?.data?.id || res?.id
          dispatch(getPreshiftDetailAction({ id }))
          history.push("/super-admin/dashboard/preshift/edit", { id })
        }
      })
    )
  }

  const handleViewOrEditPreshift = (id) => {
    dispatch(getPreshiftDetailAction({ id }))
    history.push("/super-admin/dashboard/preshift/edit", { id })
    handleMenuClose()
  }

  const handleUnpublishPreshift = (id) => {
    dispatch(
      unpublishPreshiftAction({
        id,
        callback: () => {
          dispatch(getPreshiftsAction())
          handleMenuClose()
        }
      })
    )
  }

  const handleDeletePreshift = () => {
    dispatch(
      deletePreshiftAction({
        id: selectedPreshift?.id,
        callback: () => {
          setOpenDeleteDialog(false)
          setSelectedPreshift(null)
        }
      })
    )
  }

  const handleCopyPreshift = () => {
    dispatch(
      copyPreshiftAction({
        payload: { date: selectedPreshift?.date, target_date: copySourceDate },
        callback: () => {
          setIsCopyModalOpen(false)
        }
      })
    )
  }

  const itemsPerPage = 10
  const totalPages = Math.ceil(preshiftList?.length / itemsPerPage)
  const paginatedList = preshiftList?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} className="d-flex justify-content-between align-items-center">
        <Header title="Preshift" breadCrumbs={breadCrumbs} />
        <AppButton onClick={handleAddPreshift} styles={{ padding: "14px 30px" }}>
          Add New Pre-Shift
        </AppButton>
      </Grid>
      <Grid item xs={12}>
        <TableContainer sx={{ maxHeight: 700 }}>
          <CustomTable>
            <TableHead>
              <TableRow>
                {preshiftColumns?.map((column) => (
                  <TableCell
                    key={column.id}
                    sx={{ minWidth: column?.minWidth }}
                    align={column.align}>
                    <Typography variant="body2Regular" color={theme.palette.text.secondary}>
                      {column.label}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedList?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={preshiftColumns?.length} align="center">
                    No Data Available
                  </TableCell>
                </TableRow>
              ) : (
                paginatedList?.map((row, index) => (
                  <TableRow key={row.id}>
                    <TableCell align="center">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </TableCell>
                    <TableCell align="left">{row?.date_display || "-"}</TableCell>
                    <TableCell align="center">
                      <Typography variant="body2Regular">
                        {row?.updated_by?.full_name || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2Regular">
                        {row?.updated_at
                          ? moment(row.updated_at).local().format("MMM DD, YYYY [at] hh:mm A")
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          padding: "4px 12px",
                          borderRadius: "20px",
                          display: "inline-block",
                          backgroundColor:
                            row?.status?.toUpperCase() === "PUBLISHED"
                              ? "rgba(40, 167, 69, 0.1)"
                              : "rgba(255, 193, 7, 0.1)",
                          color:
                            row?.status?.toUpperCase() === "PUBLISHED"
                              ? theme.palette.success.main
                              : theme.palette.warning.main
                        }}>
                        <Typography variant="caption">{row?.status || "-"}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <MoreHorizIcon
                        sx={{ cursor: "pointer" }}
                        onClick={(e) => handleMenuClick(e, row)}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </CustomTable>
        </TableContainer>
        {paginatedList?.length > 0 && (
          <Box className="d-flex justify-content-end mt-3">
            <AppPagination
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
            />
          </Box>
        )}
      </Grid>

      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            marginTop: "15px",
            backgroundColor: theme.palette.background.default,
            borderRadius: "12px",
            "& .MuiList-root": {
              backgroundColor: theme.palette.background.default,
              width: "200px",
              padding: "10px 0"
            }
          }
        }}>
        <StyledMenuItem onClick={() => handleViewOrEditPreshift(selectedPreshift?.id)}>
          <Typography variant="body1Regular">
            {selectedPreshift?.status.toLowerCase() === "published" ? "View" : "Edit"}
          </Typography>
        </StyledMenuItem>
        {selectedPreshift?.status.toLowerCase() === "published" && (
          <StyledMenuItem onClick={() => handleUnpublishPreshift(selectedPreshift?.id)}>
            <Typography variant="body1Regular">Make Draft</Typography>
          </StyledMenuItem>
        )}
        {selectedPreshift?.status.toLowerCase() === "published" && (
          <StyledMenuItem
            onClick={() => {
              setCopySourceDate(new Date().toISOString().split("T")[0])
              setAnchorEl(null)
              setTimeout(() => setIsCopyModalOpen(true), 100)
            }}>
            <Typography variant="body1Regular">Copy Preshift</Typography>
          </StyledMenuItem>
        )}
        <StyledMenuItem
          onClick={() => {
            setAnchorEl(null)
            setTimeout(() => setOpenDeleteDialog(true), 100)
          }}>
          <Typography sx={{ color: theme.palette.error.main }} variant="body1Regular">
            Delete
          </Typography>
        </StyledMenuItem>
      </Menu>

      <CommonModal
        open={isDateModalOpen}
        onClose={() => setIsDateModalOpen(false)}
        maxWidth="sm"
        hideAction={true}>
        <Typography variant="h2" className="mb-4 text-center" color="white">
          Choose Pre-Shift Date
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <AppTextField
              type="date"
              label="Select Date"
              value={preshiftDate}
              onChange={(e) => setPreshiftDate(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} className="d-flex justify-content-center gap-3 mt-2">
            <AppButton
              onClick={() => setIsDateModalOpen(false)}
              variant="outlined"
              sx={{ height: 48, width: 150 }}>
              Cancel
            </AppButton>
            <AppButton onClick={handleConfirmDate} sx={{ height: 48, width: 150 }}>
              Create
            </AppButton>
          </Grid>
        </Grid>
      </CommonModal>

      <CommonModal
        open={isCopyModalOpen}
        onClose={() => setIsCopyModalOpen(false)}
        maxWidth="sm"
        hideAction={true}>
        <Typography variant="h2" className="mb-4 text-center" color="white">
          Copy Pre-Shift
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <AppTextField
              type="date"
              isUpperCase={true}
              label="Source Date"
              value={selectedPreshift?.date || ""}
              onChange={(e) => setCopySourceDate(e.target.value)}
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <AppTextField
              type="date"
              isUpperCase={true}
              label="Target Date"
              value={copySourceDate}
              onChange={(e) => setCopySourceDate(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} className="d-flex justify-content-center gap-3 mt-2">
            <AppButton onClick={handleCopyPreshift} sx={{ height: 48, width: 150 }}>
              Copy
            </AppButton>
          </Grid>
        </Grid>
      </CommonModal>

      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onDelete={handleDeletePreshift}
        title="Delete Pre-Shift"
        description={`Are you sure you want to delete the pre-shift for "${
          selectedPreshift?.date_display || selectedPreshift?.date
        }"?`}
      />
    </Grid>
  )
}

export default PreshiftManagement
